// State-only landing page: /blindados/sp/, /blindados/rj/, etc.
// Aggregates every blindado in the given UF across all models. Targets the
// high-volume generic queries like "carro blindado em São Paulo" that the
// model-specific pages don't capture directly.
import { createFileRoute, notFound } from "@tanstack/react-router";
import { MARCAS, MODELOS, MIN_LISTINGS_TO_INDEX, type SeoEntry } from "@/lib/seo-config";
import { fetchSeoListings } from "@/lib/seo-data";
import { buildFaqs, buildIntro } from "@/lib/seo-copy";
import { breadcrumbJsonLd, faqJsonLd, stateItemListJsonLd, SITE_URL } from "@/lib/seo-jsonld";
import { lastUpdatedAgo } from "@/lib/seo-helpers";
import { SeoPageLayout } from "@/components/seo/SeoPageLayout";
import { LOCATIONS, SEO_LOCATIONS } from "@/lib/locations";

// Build a synthetic SeoEntry for the state. seo-copy and seo-data already
// branch on `entry.location` being set, so the existing intro/FAQ generators
// produce state-flavored copy without any new helpers.
function entryFromLocation(slug: string, displayName: string): SeoEntry {
  return {
    slug,
    displayName,
    brand: "",
    match: [],
    location: slug,
  };
}

export const Route = createFileRoute("/blindados/$location")({
  loader: async ({ params }) => {
    const locationEntry = LOCATIONS.find((l) => l.slug === params.location);
    if (!locationEntry) throw notFound();

    // "São Paulo (SP)" → "São Paulo" — strip the parenthetical UF
    const displayName = locationEntry.display_name.replace(/ \([A-Z]+\)$/, "");

    const entry = entryFromLocation(params.location, displayName);
    const { listings, stats } = await fetchSeoListings(entry);

    if (stats.total < MIN_LISTINGS_TO_INDEX) throw notFound();

    const intro = buildIntro(entry, stats);
    const faqs = buildFaqs(entry, stats);
    const updatedAgo = lastUpdatedAgo(stats.lastUpdatedAt);
    return { entry, listings, stats, intro, faqs, updatedAgo, displayName };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [{ title: "Página não encontrada" }] };
    const { entry, listings, stats, faqs, displayName } = loaderData;
    const url = `${SITE_URL}/blindados/${params.location}/`;
    const title =
      `Carros blindados em ${displayName} — ${stats.total} ofertas` +
      (stats.priceMin != null ? ` a partir de R$ ${stats.priceMin.toLocaleString("pt-BR")}` : "") +
      " | Busca Blindados";
    const description =
      `Veja ${stats.total} carros blindados à venda em ${displayName} de revendas e marketplaces.` +
      (stats.priceMin != null && stats.priceMax != null
        ? ` Preços de R$ ${stats.priceMin.toLocaleString("pt-BR")} a R$ ${stats.priceMax.toLocaleString("pt-BR")}.`
        : "") +
      " Atualizado hoje.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index,follow" },
        { property: "og:type", content: "website" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:image", content: "https://www.buscablindados.com/og-image.png" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: "https://www.buscablindados.com/og-image.png" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            stateItemListJsonLd(`Carros blindados em ${displayName}`, url, listings, stats),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(faqJsonLd(faqs)),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbJsonLd([
              { name: "Início", url: SITE_URL },
              { name: "Blindados", url: `${SITE_URL}/blindados` },
              { name: displayName, url },
            ]),
          ),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="flex min-h-screen items-center justify-center px-6 text-center">
      <div>
        <h1 className="font-display text-3xl">Sem inventário no momento</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Essa página ainda não tem ofertas suficientes. Volte em breve ou crie um alerta.
        </p>
        <a href="/" className="mt-6 inline-block text-primary underline">
          Início
        </a>
      </div>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="flex min-h-screen items-center justify-center px-6 text-center">
      <p className="text-sm text-muted-foreground">{error.message}</p>
    </div>
  ),
  component: EstadoPage,
});

function EstadoPage() {
  const { entry, listings, stats, intro, faqs, updatedAgo } = Route.useLoaderData();
  const params = Route.useParams();

  // Related: top models linked to the model+state URL so we preserve the
  // geographic context the user already chose. Plus a couple of brand pages
  // for breadth (brand pages don't have a state variant yet).
  const topModels = MODELOS.slice(0, 6).map((m) => ({
    slug: m.slug,
    displayName: m.displayName,
    kind: "modelo" as const,
    locationSlug: params.location,
  }));
  const brands = MARCAS.slice(0, 3).map((b) => ({
    slug: b.slug,
    displayName: b.displayName,
    kind: "marca" as const,
  }));
  const related = [...topModels, ...brands];

  // Cross-state links only point at SEO-eligible UFs so we never link
  // somewhere the loader would 404.
  const otherStates = SEO_LOCATIONS
    .filter((l) => l.slug !== params.location)
    .map((l) => ({ slug: l.slug, name: l.display_name.replace(/ \([A-Z]+\)$/, "") }));

  return (
    <SeoPageLayout
      data={{ entry, listings, stats }}
      kind="modelo"
      related={related}
      faqs={faqs}
      intro={intro}
      updatedAgo={updatedAgo}
      stateLinks={otherStates.length > 0 ? otherStates : undefined}
    />
  );
}
